/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import CDSLabsArticlesCollectionModule from 'bundles/cds-labs/components/Modules/ArticlesCollectionModule';
import { getModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { PromoTemplateArticlesCollectionFragment } from 'bundles/promo-template/__generated__/contentfulTypes';
import { useEstimatedReadTimesData } from 'bundles/unified-common/hooks/useEstimatedReadTimeData';

type ArticlesCollectionModuleProps = React.ComponentProps<typeof CDSLabsArticlesCollectionModule>;
type ArticlesCollectionModuleCollectionSet = ArticlesCollectionModuleProps['articleCollectionSets'][number];
type Article = ArticlesCollectionModuleCollectionSet['articles'][number];

type Props = {
  content: PromoTemplateArticlesCollectionFragment;
};

function ArticlesCollectionModule({ content }: Props) {
  const { heading, description, articleCollectionSetsCollection } = content;

  // Make a deduped list of possible slugs for the nostos query
  const slugs = new Set<string>();
  articleCollectionSetsCollection?.items?.forEach((acsc) => {
    acsc?.articlesCollection?.items?.forEach((a) => {
      if (a?.slug) {
        slugs.add(a?.slug);
      }
    });
  });

  const { loading, results } = useEstimatedReadTimesData({ slugs: Array.from(slugs) });

  const articleCollectionSets: ArticlesCollectionModuleProps['articleCollectionSets'] | undefined =
    articleCollectionSetsCollection?.items.map<ArticlesCollectionModuleCollectionSet>((articleSet) => ({
      id: articleSet?.sys.id ?? '',
      filter: articleSet?.filter ?? '',
      articles:
        articleSet?.articlesCollection?.items.map<Article>((article) => {
          let articleReadingTime;
          if (!loading && results && article?.slug && results[article.slug]) {
            articleReadingTime = results[article.slug].estimatedReadTimeLabel;
          }

          return {
            id: article?.sys.id ?? '',
            slug: article?.slug ?? '',
            title: article?.title ?? '',
            description: article?.description ?? '',
            publishedDate: article?.date,
            estimatedReadingTime: articleReadingTime,
            metadata: [],
          };
        }) ?? [],
    }));

  if (!content || !heading || !articleCollectionSets?.length) {
    // Without these fields, the content model is nonsensical
    return null;
  }

  const castArticleCollectionProps = (): ArticlesCollectionModuleProps => {
    return {
      heading,
      description: description ?? undefined,
      articleCollectionSets,
    };
  };

  return <CDSLabsArticlesCollectionModule {...getModuleLayoutConfig(content)} {...castArticleCollectionProps()} />;
}

export { ArticlesCollectionModule };

export default ArticlesCollectionModule;
